import type { ClassificationResult } from "../types/classificationResult";

export interface PublicClassificationView {
  status: "FINAL" | "NEEDS_INFO" | "STOP";
  title: string;
  hsCode?: string;
  reasoning: string;
  girPath: string;
  legalBasis: string;
  alternatives: Array<{ code: string; reason: string }>;
  missingFacts: string[];
  disclaimer: string;
}

const DISCLAIMER =
  "Napomena: Ova informacija je dobijena putem AI sistema za pomoć pri tarifnom razvrstavanju i služi isključivo u informativne svrhe. Konačnu odluku donosi nadležni carinski organ.";

const INTERNAL_LINE_PATTERNS = [
  /validator audit/i,
  /step\s*\d+:?/i,
  /mora[ ju]u/i,
  /non-high/i,
  /hs kod ne postoji/i,
  /neispravan format hs/i,
  /alternative klasifikacije/i,
];

const INTERNAL_PATTERNS = [
  /cache[ -]?[aoe]/i,
  /prema prethodnim klasifikacijama u cache/gi,
  /na osnovu prethodnih klasifikacija u cache/gi,
  /iz cache memorije/gi,
  /na osnovu cache/gi,
  /validator gate/i,
  /final_eligibility/i,
  /missing_product_facts/i,
  /runtime/i,
  /pipeline/i,
  /universalReasoning/i,
  /extractedAttributes/i,
  /foundMandatory/i,
  /missingMandatory/i,
  /decisionStatus/i,
  /blockingContradiction/i,
  /auditTrail/i,
  /engineVersion/i,
  /confidence_score/i,
  /mode_used/i,
  /tools_used/i,
  /processing_time/i,
  /from_cache/i,
  /retrieval_sources/i,
  /evidenceChain/i,
  /decisiveSource/i,
  /correlationId/i,
  /caseId/i,
  /decisionId/i,
  /validationLayers/i,
  /layer\d+_/i,
  /data_quality_fallback/i,
  /reason_codes/i,
  /version_ids/i,
  /source_ids/i,
  /PENDING_APPROVAL/i,
  /step\s*\d+:?/i,
  /validator audit/i,
  /mora[ ju]u/i,
  /non-high/i,
];

function sanitizeReasoning(text: string): string {
  // Defence-in-depth 1: drop ENTIRE internal validator/audit lines. Removing
  // marker words alone leaves residual internal prose ("HS kod ne postoji u
  // nomenklaturi") in the public reply — the whole internal line must go.
  const keptLines = text
    .split(/\r?\n/)
    .filter((line) => !INTERNAL_LINE_PATTERNS.some((pattern) => pattern.test(line)));
  let result = keptLines.join("\n");

  // Defence-in-depth 2: strip residual marker tokens from remaining lines.
  for (const pattern of INTERNAL_PATTERNS) {
    // Replace ALL occurrences - non-global regexes only remove the first match,
    // leaking e.g. "Step 2:" when "Step 1:" was the first hit in the line.
    const globalPattern = pattern.flags.includes("g") ? pattern : new RegExp(pattern.source, pattern.flags + "g");
    result = result.replace(globalPattern, "");
  }
  result = result
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
  return result;
}
export function buildPublicClassificationView(
  raw: ClassificationResult,
): PublicClassificationView {
  const sanitizedReasoning = sanitizeReasoning(raw.reasoning);
  const missingFacts = collectPublicMissingFacts(raw);

  if (raw.status === "STOP") {
    return {
      status: "STOP",
      title: "⛔ Klasifikaciju nije moguće pouzdano završiti",
      reasoning: sanitizedReasoning,
      girPath: raw.gir_path?.join(" → ") ?? "",
      legalBasis: raw.legal_basis ?? "",
      alternatives: raw.alternatives ?? [],
      missingFacts,
      disclaimer: DISCLAIMER,
    };
  }

  if (raw.status === "NEEDS_INFO") {
    return {
      status: "NEEDS_INFO",
      title: "💬 Nedovoljno podataka",
      reasoning: sanitizedReasoning,
      girPath: "",
      legalBasis: "",
      alternatives: [],
      missingFacts,
      disclaimer: DISCLAIMER,
    };
  }

  const hsCode =
    raw.hs_code && raw.hs_code !== "UNKNOWN" ? raw.hs_code : undefined;

  return {
    status: "FINAL",
    title: hsCode
      ? "📦 Prijedlog tarifnog razvrstavanja"
      : "⚠️ Procjena nije finalna",
    hsCode,
    reasoning: sanitizedReasoning,
    girPath: raw.gir_path?.join(" → ") ?? "",
    legalBasis: raw.legal_basis ?? "",
    alternatives: raw.alternatives ?? [],
    missingFacts,
    disclaimer: DISCLAIMER,
  };
}

const INTERNAL_QUESTION_PATTERNS = [
  /missing_product_facts/i,
  /validator/i,
  /gate/i,
  /runtime/i,
  /pipeline/i,
  /universalReasoning/i,
  /engineVersion/i,
  /confidence_score/i,
  /layer\d/i,
  /audit/i,
  /trace/i,
  /debug/i,
  /OCC\s*conflict/i,
  /retry/i,
  /mutation/i,
  /query/i,
  /convex/i,
  /deployment/i,
  /\b_id\b/i,
  /\b_id\b/i,
  /creation_time/i,
  /schema/i,
  /migration/i,
  /backfill/i,
  /index/i,
  /vector/i,
  /embedding/i,
  /whoops/i,
  /internal error/i,
  /unexpected error/i,
    /something went wrong/i,
    /step\s*\d+:?/i,
    /validator audit/i,
    /mora[ ju]u/i,
    /non-high/i,
    /hs kod ne postoji/i,
    /neispravan format hs/i,
    /alternative klasifikacije/i,
  ];

function isInternalQuestion(question: string): boolean {
  for (const pattern of INTERNAL_QUESTION_PATTERNS) {
    if (pattern.test(question)) return true;
  }
  return false;
}

function normalizeQuestionForDedup(question: string): string {
  return question
    .toLocaleLowerCase()
    .replace(/[^a-zšđčćž0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const DUPLICATE_SEMANTIC_GROUPS: Array<RegExp[]> = [
  [/\b(materijal|material)\b/i, /\b(materija[l]?|substance|compound)\b/i],
  [/\b(visina|height|tall)\b/i, /\b(dužina|length|long)\b/i, /\b(širina|width|wide)\b/i, /\b(debljina|thickness)\b/i],
  [/\b(težina|weight|mass|masa)\b/i],
  [/\b(boja|color|colour)\b/i],
  [/\b(oblik|shape|form)\b/i],
  [/\b(namjena|purpose|intended use|use|upotreba)\b/i],
  [/\b(marka|brand)\b/i],
  [/\b(model)\b/i],
  [/\b(godina|year|age)\b/i],
  [/\b(proizvođač|manufacturer|maker|producer)\b/i],
  [/\b(pakovanje|packaging|pack|pakiranje)\b/i],
  [/\b(količina|quantity|amount|number|broj)\b/i],
  [/\b(jačina|strength|snaga|power|voltage|napon)\b/i],
  [/\b(mjera|measure|dimension|dimenzija|veličina|size)\b/i],
  [/\b(funkcija|function|feature|karakteristika|capability)\b/i],
  [/\b(način rada|operating mode|mode of operation)\b/i],
  [/\b(sastav|composition|makeup)\b/i],
  [/\b(standard|certifikacija|certification|norma|norm)\b/i],
  [/\b(namjena|intended|svrha|purpose)\b/i],
  [/\b(oprema|equipment|pribor|accessory|dodatak|attachment)\b/i],
  [/\b(uređaj|device|apparat|apparatus|machine|mašina|stroj)\b/i],
];

function questionsAreSemanticDuplicates(a: string, b: string): boolean {
  const normA = normalizeQuestionForDedup(a);
  const normB = normalizeQuestionForDedup(b);
  if (normA === normB) return true;

  for (const group of DUPLICATE_SEMANTIC_GROUPS) {
    const aMatches = group.some((p) => p.test(normA));
    const bMatches = group.some((p) => p.test(normB));
    if (aMatches && bMatches) return true;
  }
  return false;
}

export function buildPublicClarificationQuestions(
  raw: ClassificationResult,
): string[] {
  const candidates: string[] = [];

  if (raw.missing_info?.trim()) {
    // Line-based filtering: internal validator fragments ("Step 1:", "Validator audit:")
    // are stripped per line, while user-facing questions from the same block survive.
    const lines = raw.missing_info
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    for (const line of lines) {
      if (!isInternalQuestion(line)) {
        candidates.push(line);
      }
    }
  }

  for (const q of raw.suggested_questions ?? []) {
    const trimmed = q.trim();
    if (!trimmed) continue;
    if (isInternalQuestion(trimmed)) continue;
    candidates.push(trimmed);
  }

  if (raw.validatorGate?.final_eligibility?.missing_product_facts) {
    for (const fact of raw.validatorGate.final_eligibility.missing_product_facts) {
      const trimmed = fact.trim();
      if (!trimmed) continue;
      if (isInternalQuestion(trimmed)) continue;
      candidates.push(trimmed);
    }
  }

  const seen = new Set<string>();
  const unique: string[] = [];

  for (const candidate of candidates) {
    const norm = normalizeQuestionForDedup(candidate);
    let isDuplicate = false;
    if (seen.has(norm)) {
      isDuplicate = true;
    } else {
      for (const existing of seen) {
        if (questionsAreSemanticDuplicates(candidate, existing)) {
          isDuplicate = true;
          break;
        }
      }
    }
    if (isDuplicate) continue;
    seen.add(norm);
    unique.push(candidate);
  }

  return unique.slice(0, 3);
}

function collectPublicMissingFacts(
  raw: ClassificationResult,
): string[] {
  return buildPublicClarificationQuestions(raw);
}

export function formatPublicClassificationText(
  view: PublicClassificationView,
): string {
  let text = `${view.title}\n\n`;

  if (view.status === "STOP") {
    if (view.reasoning) {
      text += `${view.reasoning}\n\n`;
    }
    return text + view.disclaimer;
  }

  if (view.status === "NEEDS_INFO") {
    if (view.missingFacts.length > 0) {
      text += "Potrebno je dopuniti sljedeće informacije:\n";
      for (const fact of view.missingFacts) {
        // Dedupe bullets: backend already prefixes "• " on multi-line missing_info
        // (e.g. chatPipeline URE follow-up questions), so strip existing bullet
        // markers per line before adding our own to avoid "• •".
        const lines = fact
          .split(/\r?\n/)
          .map((line) => line.replace(/^\s*[•\-]\s*/, "").trim())
          .filter(Boolean);
        for (const line of lines) {
          text += `• ${line}\n`;
        }
      }
      text += "\n";
    }
    if (view.reasoning && !view.missingFacts.some((f) => f === view.reasoning)) {
      text += `${view.reasoning}\n\n`;
    }
    return text + view.disclaimer;
  }

  if (view.hsCode) {
    text += `HS kod: ${view.hsCode}\n\n`;
  }

  if (view.reasoning) {
    text += `${view.reasoning}\n\n`;
  }

  if (view.girPath) {
    text += `Primijenjena GIR pravila: ${view.girPath}\n\n`;
  }

  if (view.legalBasis) {
    text += `Pravni osnov: ${view.legalBasis}\n\n`;
  }

  if (view.alternatives.length > 0) {
    text += "Alternativni kodovi:\n";
    for (const alt of view.alternatives) {
      text += `• ${alt.code}: ${alt.reason}\n`;
    }
    text += "\n";
  }

  return text + view.disclaimer;
}
